/**
 * AddPointsDialog - Dialog zum Hinzufügen von Punkten zu einem Kundenkonto
 *
 * Iter 299 (Welle A): Geschenk-Funktion
 *   • Toggle "Als Geschenk von …" blendet ein Kunden-Suchfeld ein
 *   • Bei aktivem Toggle geht die Rechnung an den Schenker, die Punkte
 *     dem Empfänger. Backend prüft die Kunden-ID via `gift_from_customer_id`.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Gift, Search, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AddPointsDialog = ({
  open,
  onOpenChange,
  pointsFormData,
  setPointsFormData,
  pointPackages,
  onAddPoints
}) => {
  const [isGift, setIsGift] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [giftSearch, setGiftSearch] = useState('');
  const [giftResults, setGiftResults] = useState([]);
  const [selectedGifter, setSelectedGifter] = useState(null);
  const [searching, setSearching] = useState(false);

  // Reset Geschenk-State wenn Dialog schließt
  useEffect(() => {
    if (!open) {
      setIsGift(false);
      setGiftSearch('');
      setGiftResults([]);
      setSelectedGifter(null);
      // gift_from_customer_id aus dem FormData entfernen
      if (pointsFormData.gift_from_customer_id) {
        const { gift_from_customer_id, ...rest } = pointsFormData;
        setPointsFormData(rest);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Wenn Toggle aus → Schenker-ID entfernen
  useEffect(() => {
    if (!isGift) {
      setSelectedGifter(null);
      if (pointsFormData.gift_from_customer_id) {
        const { gift_from_customer_id, ...rest } = pointsFormData;
        setPointsFormData(rest);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGift]);

  // Debounced Kunden-Suche
  useEffect(() => {
    if (!isGift || !giftSearch.trim() || selectedGifter) {
      setGiftResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/customers/search`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { q: giftSearch.trim(), limit: 8 },
        });
        setGiftResults(res.data || []);
      } catch (err) {
        // Fallback: /api/customers ohne Suche → manuell filtern
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/customers`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { limit: 50, search: giftSearch.trim() },
          });
          const list = Array.isArray(res.data) ? res.data : (res.data?.customers || []);
          setGiftResults(list.slice(0, 8));
        } catch (_) {
          setGiftResults([]);
        }
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [giftSearch, isGift, selectedGifter]);

  const handleSelectGifter = (cust) => {
    setSelectedGifter(cust);
    setGiftSearch('');
    setGiftResults([]);
    setPointsFormData({
      ...pointsFormData,
      gift_from_customer_id: cust.id,
    });
  };

  const handleClearGifter = () => {
    setSelectedGifter(null);
    const { gift_from_customer_id, ...rest } = pointsFormData;
    setPointsFormData(rest);
  };

  const formatCustomerLabel = (c) => {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || c.id;
    const num = c.customer_number ? ` · #${c.customer_number}` : '';
    return `${name}${num}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Punkte hinzufügen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Punktepaket auswählen (optional)</Label>
            <Select
              value={pointsFormData.package_id || 'custom'}
              onValueChange={(value) => setPointsFormData({
                ...pointsFormData, 
                package_id: value === 'custom' ? '' : value, 
                custom_points: ''
              })}
            >
              <SelectTrigger data-testid="add-points-package-select">
                <SelectValue placeholder="Paket auswählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Benutzerdefiniert</SelectItem>
                {pointPackages.map((pkg) => (
                  <SelectItem key={pkg.id} value={pkg.id}>
                    {pkg.name} - {pkg.points_amount} Punkte (CHF {pkg.price.toFixed(2)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(!pointsFormData.package_id || pointsFormData.package_id === 'custom') && (
            <>
              <div>
                <Label>Betrag in CHF</Label>
                <Input
                  type="number"
                  step="0.05"
                  min="0"
                  value={pointsFormData.amount_chf || ''}
                  onChange={(e) => setPointsFormData({ ...pointsFormData, amount_chf: e.target.value })}
                  placeholder="z.B. 120.00"
                  data-testid="add-points-amount-input"
                />
              </div>

              <div>
                <Label>Benutzerdefinierte Punktzahl</Label>
                <Input
                  type="number"
                  min="1"
                  value={pointsFormData.custom_points}
                  onChange={(e) => setPointsFormData({...pointsFormData, custom_points: e.target.value})}
                  placeholder="Anzahl Punkte"
                  data-testid="add-points-custom-input"
                />
              </div>
            </>
          )}

          <div>
            <Label>Zahlungsmethode</Label>
            <Select
              value={pointsFormData.payment_method}
              onValueChange={(value) => setPointsFormData({...pointsFormData, payment_method: value})}
            >
              <SelectTrigger data-testid="add-points-payment-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bar">Bar</SelectItem>
                <SelectItem value="rechnung_combo">Rechnung / TWINT senden</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Notiz (optional)</Label>
            <Input
              value={pointsFormData.notes}
              onChange={(e) => setPointsFormData({...pointsFormData, notes: e.target.value})}
              placeholder="z.B. Rechnungsnummer, Bemerkung"
              data-testid="add-points-notes-input"
            />
          </div>

          {/* ────────────────────────────────────────────────────────── */}
          {/* Iter 299 (Welle A): Geschenk-Funktion                       */}
          {/* ────────────────────────────────────────────────────────── */}
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 dark:bg-amber-950/20 dark:border-amber-800">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isGift}
                onChange={(e) => setIsGift(e.target.checked)}
                className="h-4 w-4 rounded text-amber-600"
                data-testid="add-points-gift-toggle"
              />
              <Gift className="h-4 w-4 text-amber-600" />
              <span className="font-medium text-sm">Als Geschenk von einem anderen Kunden</span>
            </label>
            {isGift && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-amber-900 dark:text-amber-200">
                  Die Rechnung geht an den Schenker, die Punkte dem aktuellen Kunden.
                </p>
                {selectedGifter ? (
                  <div className="flex items-center justify-between bg-white dark:bg-zinc-900 border border-amber-300 rounded p-2">
                    <div>
                      <p className="text-sm font-medium" data-testid="add-points-gifter-selected">
                        🎁 {formatCustomerLabel(selectedGifter)}
                      </p>
                      <p className="text-xs text-zinc-500">{selectedGifter.email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleClearGifter}
                      data-testid="add-points-gifter-clear-btn"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                      <Input
                        type="text"
                        placeholder="Schenker suchen (Name, E-Mail, Kundennr.)"
                        value={giftSearch}
                        onChange={(e) => setGiftSearch(e.target.value)}
                        className="pl-8"
                        data-testid="add-points-gifter-search"
                      />
                    </div>
                    {searching && (
                      <p className="text-xs text-zinc-500">Suche …</p>
                    )}
                    {!searching && giftResults.length > 0 && (
                      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded max-h-44 overflow-y-auto">
                        {giftResults.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => handleSelectGifter(c)}
                            className="w-full text-left px-3 py-2 hover:bg-amber-50 dark:hover:bg-amber-950/30 text-sm border-b last:border-b-0 border-zinc-100 dark:border-zinc-800"
                            data-testid={`add-points-gifter-option-${c.id}`}
                          >
                            <div className="font-medium">{formatCustomerLabel(c)}</div>
                            <div className="text-xs text-zinc-500">{c.email}</div>
                          </button>
                        ))}
                      </div>
                    )}
                    {!searching && giftSearch && giftResults.length === 0 && (
                      <p className="text-xs text-zinc-500">Keine Treffer.</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {pointsFormData.package_id && pointsFormData.package_id !== 'custom' && (
            <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
              <p className="text-sm font-medium text-teal-900">
                Ausgewähltes Paket: {pointPackages.find(p => p.id === pointsFormData.package_id)?.name}
              </p>
              <p className="text-xs text-teal-700 mt-1">
                {pointPackages.find(p => p.id === pointsFormData.package_id)?.points_amount} Punkte für CHF {pointPackages.find(p => p.id === pointsFormData.package_id)?.price.toFixed(2)}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="add-points-cancel-btn">
            Abbrechen
          </Button>
          <Button
            onClick={async () => {
              if (submitting) return;
              setSubmitting(true);
              try { await onAddPoints(); } finally { setSubmitting(false); }
            }}
            disabled={submitting || (isGift && !selectedGifter)}
            data-testid="add-points-submit-btn"
          >
            {submitting ? '⏳ Wird gebucht…' : (isGift && selectedGifter ? '🎁 Als Geschenk hinzufügen' : 'Punkte hinzufügen')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddPointsDialog;
